/**
 * The terminal path: commit status, claim the inject-once slot via the server's
 * atomic compare-and-set, and enqueue the directive only on a won claim.
 *
 * Called by both the poller and the live sweep (the persona may call the status
 * tool mid-conversation), which the claim makes safe to overlap (ADR 059).
 */
import type { Task } from "types/Task";
import type { TerminalTaskStatus } from "types/TaskRegistry";
import { shouldStopPolling } from "types/TaskRegistry";
import { hasResultPayoff, parseLongTaskResult } from "utilities/longTaskResult";
import {
  buildFetchResultDirective,
  buildTerminalInjectionForTask,
} from "./longTaskBuilders";
import { setLongTaskResults } from "./taskCacheWrites";
import type { EnqueueInjection } from "./usePendingInjections";
import type { ClaimTerminal, CommitTaskFn } from "./useTaskCache";

interface ApplyLongTaskTerminalDeps {
  getTask: (jobId: Task["jobId"]) => Task | undefined;
  commitTask: CommitTaskFn;
  claimTerminal: ClaimTerminal;
  enqueue: EnqueueInjection;
}

interface ApplyLongTaskTerminalOptions {
  statusReason: string | null;
  statusRaw?: string | null;
  cachedAt?: string | null;
  deps: ApplyLongTaskTerminalDeps;
}

interface ApplyFetchTaskTerminalOptions {
  result: unknown;
  deps: ApplyLongTaskTerminalDeps;
}

/**
 * Claims the terminal-directive slot, rethrowing on a transient failure so the
 * row is left non-terminal for the poller to retry (see the callers' comments
 * for why leaving it cached-terminal on a claim error would strand recovery).
 */
async function claimOrThrow(
  claimTerminal: ClaimTerminal,
  jobId: Task["jobId"],
  contextLabel: string
): ReturnType<ClaimTerminal> {
  try {
    return await claimTerminal(jobId);
  } catch (error) {
    console.error(
      `Terminal claim for ${contextLabel} failed; leaving non-terminal for retry:`,
      error
    );
    throw error;
  }
}

/**
 * Applies a terminal status transition: commits the status, claims the
 * terminal-directive slot via the server's atomic compare-and-set, and on a
 * winning claim enqueues the pending injection. Writes nothing at all when the
 * job is missing from the cache or its row was deleted server-side; commits the
 * status but skips the enqueue when another caller won the claim or the user has
 * dismissed the job's card.
 */
export async function applyLongTaskTerminal(
  jobId: string,
  status: TerminalTaskStatus,
  options: ApplyLongTaskTerminalOptions
): Promise<void> {
  const { statusReason, statusRaw, cachedAt, deps } = options;
  const job = deps.getTask(jobId);
  if (!job) return;

  // Claim before committing terminal status. A thrown claim error (transient
  // 5xx, not a lost claim) must NOT leave the row cached-terminal: if it did,
  // the poller's next-tick `shouldStopPolling` short-circuit would never retry the
  // claim, and recovery would wait for a reload. Rethrowing here leaves the row
  // non-terminal so the poller reschedules an in-session retry. A *lost* claim
  // (claimed=false: a peer tab or prior tick won) is not an error: the job is
  // terminal, so we still commit the status to converge this tab's card, but
  // skip the enqueue. Only a won claim enqueues.
  const { claimed, gone } = await claimOrThrow(
    deps.claimTerminal,
    job.jobId,
    `job ${jobId}`
  );

  // The row was deleted server-side (e.g. chat deletion) between poll and claim.
  // Skip the status commit: committing would fire a PATCH that 404s, and the
  // next focus-refetch GET drops the row anyway.
  if (gone) return;

  // Status commit comes after the claim resolves, so the awaited claim PATCH has
  // already set (or read) the claim column server-side before the status PATCH
  // is sent, so there is no PATCH-vs-claim ordering race. `applyServerTaskRow` is the
  // backstop if responses still resolve out of order.
  deps.commitTask(job.jobId, {
    status,
    statusReason: statusReason ?? undefined,
    statusRaw: statusRaw ?? job.statusRaw,
    statusCachedAt: cachedAt ?? job.statusCachedAt,
  });

  if (!claimed) return;

  // The user dismissed this job's card, so the conversation gets no completion
  // directive. The status commit above still happened, so the row is durably
  // terminal and the poller stops.
  if (job.dismissed) return;

  deps.enqueue(
    job.threadId,
    buildTerminalInjectionForTask(job, status, statusReason)
  );
}

/**
 * Applies a terminal transition for a background fetch task. On success, parses
 * the result payload, commits links to the owning job, and enqueues a
 * present-results directive. On failure with a terminal fetch body, routes
 * through `applyLongTaskTerminal`. Writes nothing when the id does not name a
 * fetch tracker row, or when that tracker carries no link to an owning job.
 */
export async function applyFetchTaskTerminal(
  fetchTaskId: string,
  status: TerminalTaskStatus,
  options: ApplyFetchTaskTerminalOptions
): Promise<void> {
  const { result, deps } = options;
  const fetchEntry = deps.getTask(fetchTaskId);
  if (!fetchEntry || !fetchEntry.fetchTaskId) return;
  // ownerJobId is the owning job's jobId (set by buildLongTaskFetchEntry).
  const ownerJobId = fetchEntry.ownerJobId;
  if (!ownerJobId) return;

  // Every exit below that does not bail early owes this commit, so it is named
  // once rather than repeated at each one.
  const dismissTracker = () =>
    deps.commitTask(fetchEntry.jobId, { status, dismissed: true });

  const fetched =
    status === "completed"
      ? parseLongTaskResult(unwrapTaskResultContent(result), "fetch")
      : null;
  const resultLinks = fetched?.links ?? [];
  const archiveUrl = fetched?.archive_url;
  const hasResults =
    !!fetched && hasResultPayoff(fetched.links, fetched.archive_url);

  if (!hasResults) {
    // A terminal fetch body with no payoff means the job failed at fetch time;
    // route the still-working owner through the terminal path. This runs before
    // the tracker is marked terminal: applyLongTaskTerminal rethrows a transient
    // claim error, and if the tracker were already cached-terminal the poller's
    // next-tick shouldStopPolling short-circuit would never retry that routing.
    if (fetched?.is_terminal) {
      const ownerJob = deps.getTask(ownerJobId);
      if (ownerJob && !shouldStopPolling(ownerJob.status)) {
        await applyLongTaskTerminal(ownerJobId, "failed", {
          statusReason: fetched.terminal_reason ?? null,
          deps,
        });
        dismissTracker();
        return;
      }
    }
    // A fetch that succeeded but whose body did not parse means the server broke
    // the envelope contract, and there is no other signal for it: the tracker is
    // dismissed, the owner keeps no links, and the card's "Retry fetch" will fail
    // the same way every time. Error level so Datadog collects it.
    if (status === "completed" && !fetched) {
      console.error(
        `Unparseable fetch payload for job ${ownerJobId} (tracker ` +
          `${fetchTaskId}); no results recorded.`,
        result
      );
    }
    dismissTracker();
    return;
  }

  const ownerJob = deps.getTask(ownerJobId);
  if (!ownerJob) {
    dismissTracker();
    return;
  }

  // Claim before committing the tracker terminal, mirroring applyLongTaskTerminal:
  // a thrown claim error (transient 5xx, not a lost claim) must not leave the
  // tracker cached-terminal, or the poller's next-tick shouldStopPolling short-circuit
  // would never retry the claim and the present-results directive would be lost
  // until a reload. Rethrowing leaves the tracker non-terminal so the poller
  // reschedules. The claim also gates the directive to inject-once: two tabs that
  // both see the fetch complete would otherwise each enqueue.
  const { claimed, gone } = await claimOrThrow(
    deps.claimTerminal,
    fetchEntry.jobId,
    `fetch tracker ${fetchTaskId}`
  );

  // The tracker row was deleted server-side (e.g. chat deletion) between poll
  // and claim. Skip both commits: they would fire PATCHes that 404, and the next
  // focus-refetch GET drops the rows anyway. Mirrors applyLongTaskTerminal.
  if (gone) return;

  dismissTracker();
  setLongTaskResults({
    jobId: ownerJob.jobId,
    resultLinks,
    archiveUrl,
    commitTask: deps.commitTask,
  });
  if (!claimed) return;

  const injection = {
    id: crypto.randomUUID(),
    chatId: fetchEntry.threadId,
    content: buildFetchResultDirective(resultLinks, archiveUrl),
    createdAt: new Date().toISOString(),
    longTaskJobId: ownerJobId,
    kind: "long-task-fetch-result" as const,
  };
  deps.enqueue(fetchEntry.threadId, injection);
}

/**
 * A task record's `result` is either the tool's result body or an MCP
 * `CallToolResult` wrapper holding it under `content`. Returns the body in both
 * cases, so the parser sees one shape.
 */
function unwrapTaskResultContent(result: unknown): unknown {
  if (result && typeof result === "object" && "content" in result) {
    return result.content;
  }
  return result;
}
